import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type { RatingSeoResultFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { CustomerRatingsShowAllFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerRatingsShowAllFragmentFragmentDoc';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { Button, useI18n } from '@packages/shared';
import { useIsBot } from '@packages/tracking';
import { useRatingTracking } from '../../Tracking/hooks/useRatingTracking';
import { customerRatingsShowAllMessages } from './messages';

/* GraphQL */ `
  fragment CustomerRatingsShowAllFragment on Product {
    sku
    availability {
      state
    }
  }
`;
export type CustomerRatingsShowAllProps = {
  data: FragmentType<typeof CustomerRatingsShowAllFragmentFragmentDoc>;
  reviewCount: NonNullable<RatingSeoResultFragmentFragment['product']>['reviewCount'];
};

export const CustomerRatingsShowAll = ({ data, reviewCount }: CustomerRatingsShowAllProps) => {
  const { sku, availability } = getFragmentData(CustomerRatingsShowAllFragmentFragmentDoc, data);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { push } = useRouter();
  const isBot = useIsBot();
  const { language, localizeUrl } = useI18n();

  const { formatMessage } = useIntl();

  const { trackGlycerinButtonClick } = useRatingTracking();

  const onClickHandler = () => {
    if (!isBot) {
      const getSearchParams = () => {
        if (!searchParams && availability.state === 'UNSELECTED') {
          return '';
        }
        if (searchParams && availability.state === 'UNSELECTED') {
          return `?${searchParams.toString()}`;
        }
        const newSearchParams = new URLSearchParams(searchParams?.toString());
        newSearchParams.set('sku', sku);
        return `?${newSearchParams.toString()}`;
      };

      trackGlycerinButtonClick({
        category: 'Alle Bewertungen anzeigen',
        detail: reviewCount.toString(),
      });

      /* 
        we need to add hash params as well
        they are needed for activating the ability to write a review for some tenants
      */
      push(localizeUrl(`${pathname}/rating${getSearchParams()}${window.location.hash}`, language), {
        scroll: true,
      });
    }
  };

  return (
    <Button
      onClick={onClickHandler}
      clickTrackingProps={null}
      sx={{ alignSelf: 'center', mt: ['auto', 'auto', 1.5] }}
    >
      {formatMessage(customerRatingsShowAllMessages.showAll, { ratingCount: reviewCount })}
    </Button>
  );
};
